/*
  A contact's phone numbers, in the order the app shows them, with duplicates removed - and, in
  contactMethods below, where they sit after the emails.

  Stated once because two places render them - the Customers dialog's Contacts tab and the Edit
  Customer Contacts dialog - and a contact whose columns disagree would otherwise read differently
  depending on which one you opened.

  Priority is Mobilephone, then Phone, then Business_phone. It decides which label a number keeps
  when two columns hold the SAME number, which is common: a contact with one line often has it
  recorded in several. The two messageable columns come first, so a number that can be chosen as
  the Preferred Channel is never hidden behind one that cannot - Business_phone is a switchboard,
  shown for reference and never messaged, which is what its label says.
*/
export const CONTACT_PHONE_COLUMNS = [
  { field: 'mobilephone', suffix: 'Mobile', channel: 'Mobilephone' },
  { field: 'phone', suffix: 'Phone', channel: 'Phone' },
  { field: 'business_phone', suffix: 'Business (not logged)', channel: null },
] as const;

/*
  The suffix beside the contact's main email - its Email column, as against the additional
  addresses after it - in the same italics as a number's Mobile / Phone. Shared with the compose
  window's To / CC list (ContactSelectionBo.IsPrimaryEmail).
*/
export const PRIMARY_EMAIL_SUFFIX = 'Primary';

export type ContactPhone = {
  value: string;
  suffix: string;
  channel: string | null;
  /* No message can go to it - see isInvalidAddress. */
  invalid: boolean;
};

/* Last 10 digits - what makes "(712) 441-1942" and "712-441-1942" the same number. */
export const phoneDigits = (value?: string | null) => value?.replace(/\D/g, '').slice(-10);

/*
  Whether the server judged this stored address unusable (story 15723): the contact's
  invalidAddresses holds each such address exactly as stored, so it is matched as stored.
*/
export const isInvalidAddress = (contact: any, value?: string | null) =>
  !!value && (contact?.invalidAddresses ?? []).includes(value);

/*
  The numbers to show for a contact, in priority order, one entry per distinct number.

  A messageable column holding no digits at all ("Viaer", a company name typed in the wrong box) is
  shown too, as itself, so it is seen and flagged invalid rather than hidden (story 15723). The
  switchboard's is not: it is never messaged, and there is nothing to fix on it for that.
*/
export function contactPhones(contact: any): ContactPhone[] {
  const seen = new Set<string>();

  return CONTACT_PHONE_COLUMNS.reduce<ContactPhone[]>((kept, { field, suffix, channel }) => {
    const value = contact?.[field];
    const key = phoneDigits(value) || (channel && value?.trim().toLowerCase());

    if (!key || seen.has(key)) return kept;

    seen.add(key);
    return [...kept, { value, suffix, channel, invalid: isInvalidAddress(contact, value) }];
  }, []);
}

export type ContactMethod =
  | ({ kind: 'phone' } & ContactPhone)
  | { kind: 'email'; value: string; suffix?: string; invalid: boolean };

/*
  Every way of reaching the contact, in the order both Email/Phone cells list them: the main email,
  then the contact's additional emails (HubSpot's "Additional email addresses"), then the numbers a
  message can go to, then Business_phone. The main email carries the Primary suffix; the
  additional ones carry none. The compose window's pick list lists a contact's addresses in the
  same order (SceRecipientService.ContactSelections).

  The main email and the numbers are also the order a single send - from SCE Reports or the
  Customers pages - messages a contact with no Preferred Channel in (Email, then Mobilephone, then
  Phone), so the top line is the address it defaults to. Business_phone is never messaged, so it
  trails as reference.
*/
export function contactMethods(contact: any): ContactMethod[] {
  const phones = contactPhones(contact).map((phone) => ({ kind: 'phone' as const, ...phone }));
  const emails: { value: string; suffix?: string }[] = [
    ...(contact?.email ? [{ value: contact.email, suffix: PRIMARY_EMAIL_SUFFIX }] : []),
    ...(contact?.additionalEmails ?? []).map((value: string) => ({ value })),
  ];

  return [
    ...emails.map((email) => ({
      kind: 'email' as const,
      ...email,
      invalid: isInvalidAddress(contact, email.value),
    })),
    ...phones.filter(({ channel }) => channel),
    ...phones.filter(({ channel }) => !channel),
  ];
}

/*
  The email the contact's Preferred marker belongs beside: the main email when Email is preferred,
  or the additional address they chose - which the server sends as preferredChannel itself (an
  address, where otherwise it sends a column name; see SceContactBo.PreferredChannel). Undefined
  when a number or nothing is preferred.
*/
export function preferredEmail(contact: any): string | undefined {
  const preferred = contact?.preferredChannel;

  if (preferred === 'Email') return contact?.email || undefined;

  return preferred?.includes('@') ? preferred : undefined;
}

/*
  Whether the contact still has the address its Preferred names: the column, holding something, or
  one of its additional emails. Nothing preferred is always on file. Contact Details can take an
  address away under an open Edit Customer Contacts row, which then has to let go of it.
*/
export function preferredOnFile(contact: any): boolean {
  const preferred = contact?.preferredChannel;

  if (!preferred) return true;
  if (preferred === 'Email') return !!contact?.email;
  if (preferred.includes('@')) return (contact?.additionalEmails ?? []).includes(preferred);

  const column = CONTACT_PHONE_COLUMNS.find(({ channel }) => channel === preferred);
  return !!phoneDigits(column && contact?.[column.field]);
}

/*
  The digits of the contact's preferred number, or undefined when an email is preferred or nothing is.

  Resolved to digits rather than compared by column, because the de-dupe above drops whichever
  column repeats a number - matching on the column alone would hide the marker exactly when two
  columns agree.
*/
export function preferredPhoneDigits(contact: any) {
  const preferred = CONTACT_PHONE_COLUMNS.find(
    ({ channel }) => channel && channel === contact?.preferredChannel
  );

  return phoneDigits(preferred && contact?.[preferred.field]);
}
